<template>
  <BaseLayout>
    <template v-slot:outer>
      <v-container>
        <v-row>
          <v-col align-self="center" cols="12" md="5" class="action-wrap">
            <div class="action">
              <h1 class="title">
                Do it all<br>
                hands-free.
              </h1>
              <p>
                LipSurf makes the web more productive, accessible, and convenient by letting you use
                your voice to dictate, click and navigate in the browser.
              </p>
              <div class="mt-7">
                <InstallBtn width="100%"/>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="7" class="anim-wrap">
            <div class="header-animation">
              <HeaderAnim
                name="girl"
                alt='A young woman sits at her desk as she speaks commands to her computer. The computer reacts according to her voice commands, e.g. when she says "down" the screen is shown scrolling down. A pleased cat is resting on her keyboard throughout.'
                :raw-anim-data="rawAnimData"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
      <v-container class="testimonials" style="max-width: 1500px">
        <v-row justify="center" class="mx-8">
          <v-col md="4" cols="12" class="d-flex justify-center" v-for="testimonial in testimonials" :key="testimonial[1]" >
              <div class="d-flex flex-column justify-space-between">
                <p>
                  {{testimonial[0]}}
                </p>
                <div class="mt-3 attribution">
                  <div>
                    <div class="mute">{{testimonial[1]}}</div>
                    <div>
                        <span title="5 stars" style="width: 110px" class="multi star"></span>
                    </div>
                  </div>
                  <div class="ml-2">
                    <span class="profile">
	<picture>
		<source type="image/avif"
			:srcset="require(`@/assets/img/${testimonial[2]}.jpg?width=60&height=60&format=avif`)"
		/>
		<img
		:src="require(`@/assets/img/${testimonial[2]}.jpg?width=60&height=60`)"
		width="60" 
		height="60"
    :alt="`photo of ${testimonial[2]}`" 
    class="circular-photo"
		/>
	</picture> 
                    </span>
                  </div>
              </div>
            </div>
          </v-col>
        </v-row>
      <v-divider class="my-10"/>
      </v-container>
      <v-container fluid class="my-2">
        <v-row row wrap class="screen-height" justify="center" align="center">
          <v-col
            sm="12"
            md="4"
            class="rel"
            v-animate-onscroll="{down: 'slideInLeft'}"
          >
            <div class="faster-block-decor"></div>
            <v-row
              column
              justify="center"
              align="center"
            >
              <v-col class="d-flex justify-center align-center">
                <div class="rel" style="z-index: 1">
                  <div class="decor" style="width: 300px; text-align: left">
                    <h2 class="font-size-1">Write Everything 3x Faster</h2>
                    <p>
                      Spend less time typing by <span class="highlight">dictating</span> instead. Works <span
                      class="highlight"
                    >anywhere</span> there's
                      editable text. We use Google's cutting edge, mind-readingly accurate and blazingly fast AI enabled speech-to-text engine.
                      <br/><br/>
                      Oh, and say goodbye to typos &#128075;
                    </p>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            sm="12"
            md="8"
            align-self="center"
            fill-height
            style="z-index: 1"
            class="rel"
            v-animate-onscroll="{down: 'slideInRight'}"
          >
          <InlineVideo name="office" alt="User is saying a phrase and it's being transcribed into a Google Docs document."/>
          </v-col>
        </v-row>
      </v-container>
      <v-container fluid class="my-16">
        <v-row column wrap class="screen-height" justify="center" align="center">
          <v-col
            sm="12"
            md="8"
            class="d-flex justify-end rel"
            style="z-index: 1"
            v-animate-onscroll="{down: 'slideInLeft'}"
          >
          <InlineVideo name="leisure" alt="User is navigating YouTube. Their spoken commands are displayed at the top of the screen as they search for a video, pause it, subscribe to the channel and like the video all using LipSurf voice commands."/>
          </v-col>
          <v-col
            sm="12"
            md="4"
          >
            <v-row
              justify-center
              align-center
              fill-height
            >
              <v-col
                class="d-flex justify-center rel"
              >
                <div class="navigate-block-decor"></div>
                <div
                  class="rel"
                  style="width: 300px; z-index: 1"
                  v-animate-onscroll="{down: 'slideInRight'}"
                >
                  <div class="decor" style="text-align: left">
                    <h2 class="font-size-1">Navigate Totally Hands-Free</h2>
                    <p>If you've never experienced the &#10024;blissful magic of hands-free web browsing,
                      you need to. <span class="highlight">Click links, scroll articles, or watch videos</span>
                      using simple voice commands.
                    </p>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
      <v-container fluid class="my-16">
        <v-row
          column
          align-content-center
          justify-center
          align-center
          fill-height
          v-animate-onscroll="{down: 'fadeIn'}"
        >
          <v-col align="center" style="max-width: 800px; margin: 0 auto">
            <div>
              <h2>Works Where You Do</h2>
              <p>Email, spreadsheets, documents, messaging apps, social media and <b>much more</b>.</p>
            </div>
          </v-col>
        </v-row>
        <v-row v-animate-onscroll="{down: 'fadeIn'}" justify="center" class="works-where mt-6">
        <figure v-for="(app, i) in apps" :key="app" v-animate-onscroll="{down: 'slideInRight', delay: i * 60}">
          <img :src="require(`@/assets/img/${app}.svg`)" class="ma-5" width="50" height="50" :title="`${app} logo`"/>
          <figcaption class="mute">
            {{app.toUpperCase().replace(/_/g, ' ')}}
          </figcaption>
        </figure>
        <figure v-animate-onscroll="{down: 'slideInRight', delay: apps.length * 100}">
          <div class="ellipsis">
            …
            </div>
            <figcaption></figcaption>
        </figure>
        </v-row>
      </v-container>
      <v-container fluid>
        <v-row row wrap class="screen-height" justify="center" align="center">
          <v-col
            sm="12"
            md="4"
            align-self-center
            fill-height
          >
            <v-row
              column
              align-content-center
              justify-center
              align-center
              fill-height
            >
              <v-col class="d-flex justify-center" style="position: relative">
                <div class="personalize-block-decor"></div>
                <div
                  class="rel"
                  style="width: 300px; text-align: left; z-index: 1"
                  v-animate-onscroll="{down: 'slideInLeft'}"
                >
                  <div class="decor">
                    <h2 class="font-size-1">Personalize to Your Needs</h2>
                    <p>
                      Map any word or phrase as a <span class="highlight alt">voice shortcut</span> and
                      quickly check the score of the game, insert a text template, 
                      customize it to understand workplace jargon, or much more.
                      </p>
                      <p class="mt-5">
                      The possibilities are limitless.
                    </p>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            sm="12"
            md="8"
            align-self-center
            class="d-flex justify-start rel"
            style="z-index: 1"
          >
            <InlineVideo name="shortcuts" alt='User is adding a shortcut to LipSurf. They make it so anytime they say "calendar" LipSurf goes to the URL where their calendar lives.'/>
          </v-col>
        </v-row>
      </v-container>
      <v-container class="d-flex justify-center align-center flex-column mt-2">
        <v-row row wrap class="ma-16">
          <v-col
            sm="12"
            md="6"
            align-self-center
            fill-height
            class="d-flex justify-center align-center"
          >
            <v-row
              column
              align-content-center
              justify-center
              align-center
              v-animate-onscroll="{down: 'slideInLeft'}"
            >
              <v-col class="d-flex justify-center align-center">
                <div>
	<picture>
		<source type="image/webp"
			:srcset="require(`@/assets/img/hands.png?width=233&height=179&format=webp`)"
		/>
		<img
		:src="require(`@/assets/img/hands.png?width=233&height=179`)"
		width="233" 
    alt="hands in pain"
		height="179"
		loading="lazy"
		/>
	</picture> 
                </div>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            sm="12"
            md="6"
            align-self-center
            class="d-flex justify-center align-center"
          >
            <div class="feature"
              v-animate-onscroll="{down: 'slideInRight'}"
            >
                <h2>Prevent RSI/Carpal Tunnel Syndrome</h2>
                <p>
                  Taking breaks from the keyboard is the most effective way of
                  <span class="highlight">healing or preventing</span> office-related repetitive
                  stress injuries.
                </p>
                <div style="text-align: right">
                <div
                  class="mt-5 font-italic font-light"
                >
                  “An ounce of prevention <br>is worth a pound of cure.” 
                </div><div class="mute">-Every therapist</div>
                </div>
            </div>
          </v-col>
        </v-row>
        <v-row row wrap class="ma-12">
          <v-col
            sm="12"
            md="6"
            align-self-center
            class="d-flex justify-center align-center"
          >
            <div class="feature"
              v-animate-onscroll="{down: 'slideInLeft'}"
            >
                <h2>Multi-task</h2>
                <p>
                  You can &#127829; eat, &#129496; do yoga, &#129526; knit or even nurse your baby while you get work
                  done.  Having your hands free in front of your computer opens up countless (even strange) possibilities.
                </p>
            </div>
          </v-col>
          <v-col
            sm="12"
            md="6"
            align-self-center
            fill-height
            class="d-flex justify-center align-center"
          >
            <v-row
              column
              align-content-center
              justify-center
              align-center
            >
              <v-col
                class="d-flex justify-center align-center"
                v-animate-onscroll="{down: 'slideInRight'}"
              >
                <div>
	<picture>
		<source type="image/webp"
			:srcset="require(`@/assets/img/yoga-cat.png?width=271&height=311&format=webp`)"
		/>
		<img
		:src="require(`@/assets/img/yoga-cat.png?width=271&height=311`)"
    alt="cat doing yoga in front of a laptop"
		width="271" 
		height="311"
		loading="lazy"
		/>
	</picture> 
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row row wrap class="ma-12">
          <v-col
            sm="12"
            md="6"
            align-self-center
            fill-height
            class="d-flex justify-center align-center"
          >
            <v-row
              column
              align-content-center
              justify-center
              align-center
            >
              <v-col
                class="d-flex justify-center align-center"
                v-animate-onscroll="{down: 'slideInLeft'}"
              >
                <div>
	<picture>
		<source type="image/webp"
			:srcset="require(`@/assets/img/data-cat.png?width=241&height=369&format=webp`)"
		/>
		<img
		:src="require(`@/assets/img/data-cat.png?width=241&height=369`)"
		width="241" 
    alt="cat with sunglasses on a sketchy streetcorner opening a cloak that says &quot;user data&quot; inside"
		height="369"
		loading="lazy"
		/>
	</picture> 
                </div>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            sm="12"
            md="6"
            align-self-center
            class="d-flex justify-center align-center"
          >
            <div
              class="feature"
              v-animate-onscroll="{down: 'slideInRight'}"
            >
                <h2 style="font-size: 1rem">No tracking, no ads, no selling your data</h2>
                <p>
                  We make money directly from subscriptions. Not by selling your data.
                </p>
              </div>
          </v-col>
        </v-row>
      </v-container>
      <v-container>
        <v-divider class="my-2"/>
      </v-container>
      <v-container class="d-flex justify-center flex-column align-center">
        <v-row align="center" justify="center">
          <v-col xs12 md12>
            <h2 style="text-align: center">It’s not Siri, Alexa or Google Home</h2>
          </v-col>
        </v-row>
        <div>
          <v-row justify="center" align="start">
            <v-col
              xs12
              md6
              class="d-flex justify-center align-center"
              v-animate-onscroll="{down: 'fadeIn'}"
            >
              <div style="width: 300px; position: relative;" class="its-not">

	<picture>
		<source type="image/webp"
			:srcset="require(`@/assets/img/surfboards.png?width=300&height=685&format=webp`)"
		/>
		<img
		:src="require(`@/assets/img/surfboards.png?width=300&height=685`)"
		height="685"
		width="300" 
		loading="lazy"
    alt="surfboards" 
    style="position: absolute; z-index: 0; left: -320px; bottom: 0px"
		/>
	</picture> 
                <p>
                  Siri, Alexa, and Google Home work great in a pinch for checking
                  the weather or setting a timer - but they don't work for humanity's
                  most important platform, the web.
                </p>
                <p>
                  The web is the only truly ubiquitous, and open technology not controlled
                  by any single gatekeeper. With the web already having the apps we use daily,
                  and with voice being the next great computer interface (following the
                  keyboard and mouse), we can have a more productive, accessible, and
                  convenient life when both are combined.
                </p>
                <p>
                  Sometimes collaboration beats
                  competition. We're collaborating
                  with our beloved web to bring it
                  supercharged productivity - letting
                  you dictate and navigate anywhere
                  on it with just your voice.
                </p>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-container>
      <v-container>
        <v-divider class="my-16"/>
        <v-row align="center" justify-content="center">
          <v-col
            justify="center"
            v-animate-onscroll="{down: 'slideInLeft'}"
            md="4"
            cols="12"
          >
            <div class="description d-flex flex-column justify-space-between">
              <div>
              <h2 class="description__head">Open-Source Plugins</h2>
              <p>
                Make deep integrations.
                Everything is a plugin in LipSurf
                and anyone can make a plugin
                for LipSurf - it's fully extensible.
              </p>
              </div>
              <a href="https://docs.lipsurf.com" class="d-flex mt-5">Developer Docs &#8250;</a>
            </div>
          </v-col>
          <v-col
            justify="center"
            v-animate-onscroll="{down: 'fadeIn'}"
            md="4"
            cols="12"
          >
            <div class="description d-flex flex-column justify-space-between">
              <div>
              <h2 class="description__head">Multi-lingual</h2>
              <div>
                <p>🇺🇸 English</p>
                <p>🇪🇸 Español Spanish</p>
                <p>🇫🇷 Français French</p>
                <p>🇷🇺 Русский Russian</p>
                <p>🇯🇵 日本語 Japanese</p>
              </div>
              </div>
              <a href="https://help.lipsurf.com/configuration/adding-languages.html#adding-languages">More Languages &#8250;</a>
            </div>
          </v-col>
          <v-col
            justify="center"
            v-animate-onscroll="{down: 'slideInRight'}"
            md="4"
            cols="12"
          >
            <div class="description">
              <h2 class="description__head">No special hardware required</h2>
                <p>
                  No need to buy a Google Home or Alexa.
                  LipSurf works with with your existing computer
                  hardware and microphone
                  <span class="highlight">(Windows, Mac, Linux and even Chromebooks)</span>.
                </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
      <div>
        <div
          class="info my-16"
          v-animate-onscroll="{down: 'fadeIn'}"
        >
          <div class="skewed-block"></div>
          <div class="info__content-block pa-5">
            <h2>Is LipSurf right for me?</h2>
            <v-row justify="center" align="start" wrap row>
              <v-col sm12 md6 class="d-flex justify-center flex-column align-center">
                <div class="info-item">
                  <div class="info-item__img d-flex justify-center align-center">
                    <img :src="require('@/assets/img/wheelchair-woman.png')" width="100" height="91" alt="woman in a wheelchair" loading="lazy">
                  </div>
                  <h5>Motor Disabilities</h5>
                    <p>
                      We make the web more accessible
                      for people with Parkinson's, Arthritis, Cerebral Palsey,
                      RSI, Carpal Tunnel Syndrome, Essential tremor,
                      amputees and other motor disabilities.
                    </p>
                </div>
              </v-col>
              <v-col sm12 md6 class="d-flex justify-center flex-column align-center">
                <div class="info-item">
                  <div class="info-item__img pa-3 d-flex justify-center align-center">
                    <img :src="require('@/assets/img/santa.png')" width="100" height="91" alt="wizard" loading="lazy">
                  </div>
                  <h5>Productivity Nerds</h5>
                    <p>
                      Want to write 3x faster than you type?
                      ❤️ shortcuts? Want to control the computer
                      without reaching to the keyboard?
                    </p>
                </div>
              </v-col>
              <v-col sm12 md6 class="d-flex justify-center flex-column align-center">
                <div class="info-item">
                  <div
                    class="info-item__img pa-3 d-flex justify-center align-center"
                  >
                    <img :src="require('@/assets/img/programmer.png')" width="100" height="91" alt="person on a laptop" loading="lazy">
                  </div>
                  <h5>Computer Professionals</h5>
                    <p>
                      Office workers, teachers, executives, project managers,
                      bloggers, and other professionals who need to type
                      every day.
                    </p>
                </div>
              </v-col>
              <v-col sm12 md6 class="d-flex justify-center flex-column align-center">
                <div class="info-item">
                  <div class="info-item__img pa-3 d-flex justify-center align-center">
                    <img :src="require('@/assets/img/brain.png')" width="100" height="91" alt="brain" loading="lazy">
                  </div>
                  <h5>Neurodiverse</h5>
                    <p>
                      People with Dyslexia and/or ADHD.
                    </p>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
      <section class="center mt-16">
        <h4>
          <span class="font-regular" style="font-size: 1.1rem">LipSurf works through Google Chrome.</span><br/>
          <span style="font-size: 1.1rem">Simply add it and you’ll be using voice-commands <strong>30 seconds</strong> from now.</span>
        </h4>
        <div>
          <InstallBtn/>
        </div>
	<picture>
		<source type="image/webp"
			:srcset="require(`@/assets/img/kb-cat-2.png?width=201&height=131&format=webp`)"
		/>
		<img
		:src="require(`@/assets/img/kb-cat-2.png?width=201&height=131`)"
		width="201" 
		height="131"
		loading="lazy"
    alt="cat cleaning self on keyboard" 
    aria-role="presentation"
    class="mt-6"
		/>
	</picture> 
      </section>
    </template>
  </BaseLayout>
</template>

<style scoped>
  @import "../css/landing-shared.css";
.title {
  margin-bottom: 0.3em;
  line-height: 1.2em;
  font-weight: 800;
  font-size: 3.1em;
}
.action-wrap {
  order: 1;
  z-index: 1;
  text-align: center;
  margin: 0 auto;
  flex: 0 1;
}

.header-animation {
  margin: -50px 0 -100px 0;
  transform: translateX(-30px);
}

@media screen and (min-width: 960px) {
  .action-wrap {
    text-align: left;
  }

  .header-animation {
    margin: -200px -250px -100px -460px;
    transform: none;
  }
}

.anim-wrap {
  order: 2;
  z-index: 0;
}

.action {
  width: 320px;
}

.attribution {
  display: flex;
  justify-content: flex-end;
}

.attribution > div {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.attribution .circular-photo {
  width: 60px;
  height: 60px;
}

.its-not > p {
  margin-top: 1em;
  z-index: 10;
  position: relative;
}

.info {
  position: relative;
  min-height: 500px;
}

.info h2 {
    text-align: center;
    padding: 20px 0;
}

.info h5 {
  font-size: 1.1em;
}

.info .skewed-block {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: #464646;
    transform: skewY(-3deg);
}

.info__content-block {
    position: relative;
    z-index: 1;
    color: #fff;
    max-width: 80%;
    margin: 0 auto;
}

.info-item {
    max-width: 280px;
}

.info-item p {
  color: #dedede;
}

.info-item__img {
    width: 100px;
    height: 100px;
    overflow: hidden;
    background-color: #ffffff;
    border-radius: 10px;
}

.description {
    max-width: 500px;
    margin: 0 auto;
    min-height: 240px;
}

.description__head {
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0 0 0.5em 0;
}

.feature {
    max-width: 500px; 
    min-width: 300px; 
    text-align: left;
}

.feature h2 {
  font-size: 1.2em;
}

.works-where {
  font-size: 0.7em;
  text-align: center;
}
.works-where figcaption {
  max-width: 100px;
}
.ellipsis {
  display: flex;
  text-align: center;
  align-self: center;
  margin: 1.2em;
  font-size: 1.5em;
  font-weight: 800;
}
</style>

<script lang="ts" setup>
import BaseLayout from './BaseLayout.vue';
import InlineVideo from '@/components/InlineVideo.vue';
import HeaderAnim from '../components/HeaderAnim.vue';
import InstallBtn from '../components/InstallBtn.vue';
import rawAnimData from '../assets/anim/girl.json';

const testimonials = [
  [
    "I had LipSurf up and running... within 5 - 10 minutes. It is amazing. It works in the Chrome browser... and Dragon never did.",
    "Jessica DeVita",
    "Jessica",
  ],
  [
    "Couldn't be happier right now - I've been looking for something like this for ages!",
    "Steve Whitehead",
    "Steve",
  ],
  [
    "[I]t has been absolutely life-changing as I am dyslexic it helped me a great deal and with the support I have received has been amazing!",
    "Shea Conrad",
    "Shea",
  ],
];
const apps = [
  'gmail',
  'outlook',
  'google_docs',
  'google_sheets',
  'facebook',
  'linkedin',
  'microsoft_word',
  'youtube',
  'reddit',
  'slack',
  'twitter',
  'discord',
  'whatsapp',
  'evernote',
  'salesforce'
];
</script>